import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'sonner';
import {useGetCampaignById, usePublishCampaignLandingPage, useStartLandingPageGeneration} from '@tinct/core-api';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {Step3EditContent} from '../step3-edit';
import {LaunchLandingPageModal} from '../modals/launch-landing-page-modal';

type UseCreateStep3EditOptions = {
  campaignId: string;
};

/**
 * Owns the edit step's top-bar actions: Publish opens the launch modal, whose
 * confirm awaits the synchronous publish and flips the modal into its live
 * confirmation (URLs + hand-off to the variant phase — there is no publishing
 * step for NEW-origin campaigns). Regenerate re-enters building.
 */
export const useCreateStep3Edit = ({campaignId}: UseCreateStep3EditOptions) => {
  const navigate = useNavigate();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  // Post-publish confirmation state. URLs come from the publish RESPONSE —
  // on a first-ever publish the cached campaign still has them null.
  const [launchedUrls, setLaunchedUrls] = useState<{
    publishedUrl: string | null;
    organizationPublishedUrl: string | null;
  } | null>(null);
  const launched = launchedUrls !== null;

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        landingPage: {
          fields: {
            id: null,
            status: null,
            publishedUrl: null,
            organizationPublishedUrl: null,
            currentVersion: {
              fields: {
                id: null,
              },
            },
          },
        },
      },
    },
    {
      enabled: !!campaignId,
      // This query caches separately from the edit content's (fields are part
      // of the key), so it must recover on its own: an applied-edit
      // invalidation can catch a transient non-ready status and, with the
      // default staleTime of Infinity, freeze Publish disabled. Poll until the
      // page is publishable again; `failed` stops it (regenerate restarts).
      refetchInterval: (query) => {
        const lp = query.state.data?.landingPage;
        const publishable = lp?.status === 'ready' && !!lp.currentVersion;
        return lp && !publishable && lp.status !== 'failed' ? 2000 : false;
      },
    }
  );

  const landingPage = campaign?.landingPage;
  const canPublish = landingPage?.status === 'ready' && !!landingPage.currentVersion;
  const publishDisabledReason = canPublish
    ? null
    : landingPage?.status === 'failed'
      ? 'Page generation failed — regenerate the page to publish'
      : 'Your page is still being prepared — publish unlocks when it is ready';

  const {mutateAsync: publishLandingPage, isPending: isPublishing} =
    usePublishCampaignLandingPage();
  const {mutate: startGeneration} = useStartLandingPageGeneration();

  const openLaunchModal = useCallback(() => setShowLaunchModal(true), []);

  const handleConfirmLaunch = useCallback(async () => {
    try {
      const result = await publishLandingPage({id: campaignId});
      // Keep the modal open in its confirmation state instead of bouncing
      // straight to the details page with only a toast.
      setLaunchedUrls({
        publishedUrl:
          result?.landingPage?.publishedUrl ??
          landingPage?.publishedUrl ??
          null,
        organizationPublishedUrl:
          result?.landingPage?.organizationPublishedUrl ??
          landingPage?.organizationPublishedUrl ??
          null,
      });
    } catch {
      toast.error('Could not publish your landing page. Please try again.');
    }
  }, [campaignId, publishLandingPage, landingPage]);

  const exitToCampaign = useCallback(() => {
    navigate(`${currentWorkspaceUrlPrefix}/campaigns/${campaignId}`, {replace: true});
  }, [campaignId, currentWorkspaceUrlPrefix, navigate]);

  const goToVariantPhase = useCallback(() => {
    navigate(`${currentWorkspaceUrlPrefix}/funnel-campaigns/${campaignId}/audience`, {replace: true});
  }, [campaignId, currentWorkspaceUrlPrefix, navigate]);

  const regenerate = useCallback(() => {
    startGeneration(
      {id: campaignId},
      {
        onSuccess: () => {
          navigate(`${currentWorkspaceUrlPrefix}/funnel-campaigns/${campaignId}/building`, {
            replace: true,
          });
        },
        onError: () => {
          toast.error('Could not start the regeneration. Please try again.');
        },
      }
    );
  }, [campaignId, currentWorkspaceUrlPrefix, navigate, startGeneration]);

  const component = useCallback(
    () => (
      <>
        <Step3EditContent campaignId={campaignId} onRegenerate={regenerate} />
        <LaunchLandingPageModal
          open={showLaunchModal}
          onOpenChange={(open) => {
            setShowLaunchModal(open);
            // Dismissing the post-launch confirmation exits like "Go to
            // campaign" — the edit step is over once the page is live.
            if (!open && launched) exitToCampaign();
          }}
          publishedUrl={launchedUrls?.publishedUrl ?? landingPage?.publishedUrl ?? null}
          organizationPublishedUrl={launchedUrls?.organizationPublishedUrl ?? null}
          isPublishing={isPublishing}
          launched={launched}
          onLaunch={handleConfirmLaunch}
          onGoToCampaign={exitToCampaign}
          onCreateVariants={goToVariantPhase}
        />
      </>
    ),
    [campaignId, showLaunchModal, landingPage?.publishedUrl, launchedUrls, launched, isPublishing, handleConfirmLaunch, exitToCampaign, goToVariantPhase, regenerate]
  );

  return {
    component,
    openLaunchModal,
    canPublish,
    publishDisabledReason,
    isPublishing,
    regenerate,
  };
};
